import React, { useEffect, useState } from "react";
import { Image, notification } from "antd";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import LoadingEffect from "../../../components/Loading/LoadingEffect";
import ImageUploader from "../../../components/ImageUploader/ImgUploader";
import { ASSETS_BASE_URL } from "../../../config/constants";
import {
  getContent,
  saveContent,
} from "../../../controllers/V1/raffleCoupon/raffleCouponContentController";

const DEFAULT_FORM = {
  page_title: "Buy & Win Raffle Coupon",
  logo_image: "/old_logo.png",
  logo_alt: "Orbit Kuwait",
  raffle_background_image: "/images/buy-win-raffle.png",
  promo_text: "Get a free raffle coupon with every product you purchase!",
  brand_title: "Orbit Kuwait",
  brand_subtitle: "Orbit Kuwait",
  terms_title: "Terms & Conditions",
  terms_items: [
    "Coupon for every purchase above 10 KD",
    "Bring original invoice and raffle coupon",
  ],
  show_promotion_dates: "Y",
  promotion_label: "Promotion",
  contact_mobile: "+965 6600 0000",
  contact_location: "Shop No. 1, Road 1, Block 1, Kuwait",
  side_note: "* Terms and conditions apply",
  status: "A",
};

const resolveImageSrc = (path = "") => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${ASSETS_BASE_URL}${path.replace(/^\//, "")}`;
};

const RaffleCouponContentAddEdit = () => {
  const [data, setData] = useState(DEFAULT_FORM);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [showBgUploader, setShowBgUploader] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTermChange = (index, value) => {
    setData((prev) => {
      const terms = [...(prev.terms_items || [])];
      terms[index] = value;
      return { ...prev, terms_items: terms };
    });
  };

  const addTermItem = () => {
    setData((prev) => ({
      ...prev,
      terms_items: [...(prev.terms_items || []), ""],
    }));
  };

  const removeTermItem = (index) => {
    setData((prev) => ({
      ...prev,
      terms_items: (prev.terms_items || []).filter((_, i) => i !== index),
    }));
  };

  const validateFormData = () => {
    const newErrors = {};
    let isValid = true;

    if (!data.page_title?.trim()) {
      newErrors.page_title = "Page title is required.";
      isValid = false;
    }
    if (!data.promo_text?.trim()) {
      newErrors.promo_text = "Promo text is required.";
      isValid = false;
    }
    if (!data.contact_mobile?.trim()) {
      newErrors.contact_mobile = "Contact mobile is required.";
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  const fetchContent = async () => {
    try {
      setIsLoading(true);
      const res = await getContent();
      if (res.status === true && res.result) {
        setData({
          ...DEFAULT_FORM,
          ...res.result,
          terms_items: res.result.terms_items?.length
            ? res.result.terms_items
            : DEFAULT_FORM.terms_items,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateFormData()) return;

    try {
      setIsLoading(true);
      const payload = {
        ...data,
        terms_items: (data.terms_items || []).filter((item) => item?.trim()),
      };
      const res = await saveContent(payload);
      if (res.status === true) {
        notification.open({
          message: "Success.",
          description: "Raffle coupon content saved successfully.",
          placement: "topRight",
          icon: <CheckCircleOutlined style={{ color: "green" }} />,
          duration: 2,
        });
        if (res.result) {
          setData({
            ...DEFAULT_FORM,
            ...res.result,
            terms_items: res.result.terms_items?.length
              ? res.result.terms_items
              : DEFAULT_FORM.terms_items,
          });
        }
      } else {
        notification.open({
          message: "Error.",
          description: res.message || "Unable to save content.",
          placement: "topRight",
          icon: <InfoCircleOutlined style={{ color: "red" }} />,
          duration: 2,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Orbit Kuwait || Raffle Coupon Content";
    fetchContent();
  }, []);

  const textFields = [
    { label: "Page Title", name: "page_title", required: true, size: "6" },
    { label: "Logo Alt Text", name: "logo_alt", size: "6" },
    { label: "Brand Title", name: "brand_title", size: "4" },
    { label: "Brand Subtitle", name: "brand_subtitle", size: "4" },
    { label: "Terms Title", name: "terms_title", size: "4" },
    { label: "Promotion Label", name: "promotion_label", size: "4" },
    { label: "Contact Mobile", name: "contact_mobile", required: true, size: "4" },
    { label: "Side Note", name: "side_note", size: "4" },
    { label: "Contact Location", name: "contact_location", size: "12" },
  ];

  return (
    <div className="container-fluid">
      {isLoading && <LoadingEffect />}
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Buy & Win Raffle Coupon Content</h4>
              <Link to="/dashboard" className="btn btn-sm btn-secondary">
                <ArrowLeftOutlined /> Back
              </Link>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {textFields.map(({ label, name, required, size }) => (
                    <div
                      key={name}
                      className={`form-group col-lg-${size} col-md-${size} col-sm-12 mb-3`}
                    >
                      <label>
                        {required && <span style={{ color: "red" }}>*</span>} {label}:
                      </label>
                      <input
                        type="text"
                        name={name}
                        value={data[name] || ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                      {error[name] && (
                        <small style={{ color: "red" }}>{error[name]}</small>
                      )}
                    </div>
                  ))}

                  <div className="form-group col-lg-12 mb-3">
                    <label>
                      <span style={{ color: "red" }}>*</span> Promo Text:
                    </label>
                    <textarea
                      name="promo_text"
                      value={data.promo_text || ""}
                      onChange={handleChange}
                      className="form-control"
                      rows={3}
                    />
                    {error.promo_text && (
                      <small style={{ color: "red" }}>{error.promo_text}</small>
                    )}
                  </div>

                  <div className="form-group col-lg-4 mb-3">
                    <label>Show Promotion Dates:</label>
                    <select
                      name="show_promotion_dates"
                      value={data.show_promotion_dates || "Y"}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div className="form-group col-lg-4 mb-3">
                    <label>Status:</label>
                    <select
                      name="status"
                      value={data.status || "A"}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="A">Active</option>
                      <option value="I">Inactive</option>
                    </select>
                  </div>

                  <div className="form-group col-lg-6 mb-3">
                    <label>Logo Image:</label>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowLogoUploader(true)}
                      >
                        Upload Logo
                      </button>
                      {data.logo_image && (
                        <Image
                          src={resolveImageSrc(data.logo_image)}
                          alt="Logo preview"
                          width={80}
                          preview
                        />
                      )}
                    </div>
                    <input
                      type="text"
                      name="logo_image"
                      value={data.logo_image || ""}
                      onChange={handleChange}
                      className="form-control mt-2"
                      placeholder="/old_logo.png or uploaded image path"
                    />
                  </div>

                  <div className="form-group col-lg-6 mb-3">
                    <label>Raffle Background Image:</label>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowBgUploader(true)}
                      >
                        Upload Background
                      </button>
                      {data.raffle_background_image && (
                        <Image
                          src={resolveImageSrc(data.raffle_background_image)}
                          alt="Background preview"
                          width={120}
                          preview
                        />
                      )}
                    </div>
                    <input
                      type="text"
                      name="raffle_background_image"
                      value={data.raffle_background_image || ""}
                      onChange={handleChange}
                      className="form-control mt-2"
                      placeholder="/images/buy-win-raffle.png or uploaded image path"
                    />
                  </div>

                  <div className="form-group col-lg-12 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="mb-0">Terms & Conditions Items:</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={addTermItem}
                      >
                        <PlusOutlined /> Add Item
                      </button>
                    </div>
                    {(data.terms_items || []).map((item, index) => (
                      <div key={index} className="d-flex gap-2 mb-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => handleTermChange(index, e.target.value)}
                          className="form-control"
                          placeholder={`Term item ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeTermItem(index)}
                          disabled={(data.terms_items || []).length <= 1}
                        >
                          <DeleteOutlined />
                        </button>
                      </div>
                    ))}
                    <small className="text-muted">
                      Promotion dates are added automatically when enabled and draw data is available.
                    </small>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  Save Content
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ImageUploader
        openUpload={showLogoUploader}
        toggleUploadOpen={() => setShowLogoUploader(false)}
        setImgUrl={(url) => setData((prev) => ({ ...prev, logo_image: url }))}
      />
      <ImageUploader
        openUpload={showBgUploader}
        toggleUploadOpen={() => setShowBgUploader(false)}
        setImgUrl={(url) => setData((prev) => ({ ...prev, raffle_background_image: url }))}
      />
    </div>
  );
};

export default RaffleCouponContentAddEdit;
